import { parseQuickAdd, type QuickAddContext, type QuickAddResult, type TokenType } from "@letsdoit/quick-add";
import type { SyncState } from "../sync/state";

export interface Segment {
  text: string;
  start: number;
  type: TokenType | null;
  /** Priority tokens carry their level for coloring. */
  priority?: number;
}

/** Split text into plain and token segments for the highlighting overlay. */
export function highlightSegments(text: string, result: Pick<QuickAddResult, "tokens">): Segment[] {
  const out: Segment[] = [];
  let pos = 0;
  for (const t of result.tokens) {
    if (t.start < pos || t.end > text.length) continue;
    if (t.start > pos) out.push({ text: text.slice(pos, t.start), start: pos, type: null });
    const seg: Segment = { text: text.slice(t.start, t.end), start: t.start, type: t.type };
    if (t.type === "priority") seg.priority = t.priority;
    out.push(seg);
    pos = t.end;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), start: pos, type: null });
  return out;
}

/** Quick Add parsing context from the local store (projects, sections, labels, collaborators). */
export function quickAddContext(state: SyncState, now = new Date()): QuickAddContext {
  const user = state.user;
  return {
    projects: Object.values(state.projects).map((p) => ({ id: p.id, name: p.name })),
    sections: Object.values(state.sections).map((s) => ({ id: s.id, name: s.name, projectId: s.project_id })),
    labels: Object.values(state.labels).map((l) => l.name),
    collaborators: Object.values(state.collaborators).map((c) => ({ id: c.id, name: c.full_name, email: c.email })),
    defaultProjectId: user?.inbox_project_id ?? null,
    dates: {
      now,
      userTimezone: user?.tz_info.timezone ?? "UTC",
      startDay: user?.start_day ?? 1,
      nextWeek: user?.next_week ?? 1,
      weekendStartDay: user?.weekend_start_day ?? 6,
    },
  };
}

export function parseForHighlight(text: string, ctx: QuickAddContext): QuickAddResult {
  try {
    return parseQuickAdd(text, ctx);
  } catch {
    return {
      content: text,
      description: null,
      project: null,
      section: null,
      labels: [],
      priority: null,
      assignee: null,
      due: null,
      deadline: null,
      duration: null,
      reminders: [],
      tokens: [],
    };
  }
}
